import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation } from "@reach/router";
import { FC } from "react";
import ShiButton from "src/atoms/ShiButton";
import { getMigrationProductName } from "src/lib/getMigrationProductName";
import {
  InfoIcon,
  MigrationBuildingIcon,
  MigrationImageIcon,
  MigrationPackageIcon,
  MigrationUsersIcon,
} from "./elements";

type MigrationItem = {
  label: string;
  Icon: FC;
};

const MIGRATION_ITEMS: MigrationItem[] = [
  {
    label: "Informations entreprise (SIRET, adresse)",
    Icon: MigrationBuildingIcon,
  },
  { label: "Logo entreprise", Icon: MigrationImageIcon },
  { label: "Articles", Icon: MigrationPackageIcon },
  { label: "Contacts", Icon: MigrationUsersIcon },
];

type MigrationModalProps = {
  isOpen: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const MigrationModal = ({
  isOpen,
  isSuccess,
  isLoading,
  onClose,
  onConfirm,
}: MigrationModalProps) => {
  const { pathname } = useLocation();
  const productName = getMigrationProductName(pathname) ?? "";

  return (
  <Modal isOpen={isOpen} onClose={onClose} motionPreset="scale" isCentered>
    <ModalOverlay />
    <ModalContent
      borderRadius="30px"
      border="14px solid"
      borderColor="btn-yellow-bg"
      maxWidth={{ base: "95vw", md: "800px" }}
    >
      <ModalCloseButton top="20px" right="20px" />
      <ModalBody padding={{ base: "space-24", md: "space-32" }}>
        {isSuccess ? (
          <VStack align="stretch" spacing="space-24">
            <Text variant="body-sm-reg" color="gray.500" fontWeight="medium">
              {productName} → Shine Facture
            </Text>

            <Heading as="h2" variant="heading-2">
              Votre évolution vers Shine Facture est en cours&nbsp;!
            </Heading>

            <Text variant="body-md-reg">Merci pour votre confiance.</Text>

            <Text variant="body-md-reg">
              L&apos;activation de votre compte Shine Facture et le transfert de
              vos données sont en cours.
            </Text>

            <Text variant="body-md-reg">
              Dans les prochaines minutes, vous recevrez un e-mail de
              confirmation vous invitant à créer votre mot de passe Shine
              Facture et à vous connecter à votre espace. Si vous ne le trouvez
              pas dans votre boîte de réception, pensez à vérifier votre dossier
              de courriers indésirables (spams).
            </Text>
          </VStack>
        ) : (
          <VStack align="stretch" spacing="space-24">
            <Text variant="body-sm-reg" color="gray.500" fontWeight="medium">
              {productName} → Shine Facture
            </Text>

            <Heading as="h2" variant="heading-2">
              Confirmer votre évolution
            </Heading>

            <Text variant="body-md-reg">
              Les données suivantes seront importées dans votre nouveau compte
              Shine Facture. Après avoir confirmé votre évolution, nous vous
              conseillons vivement de ne plus utiliser {productName} pour ne
              pas perdre de données via de la double saisie.
            </Text>

            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              overflow="hidden"
            >
              {MIGRATION_ITEMS.map(({ label, Icon }, index) => (
                <Flex
                  key={label}
                  align="center"
                  gap="space-16"
                  px="space-24"
                  py="space-16"
                  borderBottom={
                    index < MIGRATION_ITEMS.length - 1 ? "1px solid" : "none"
                  }
                  borderColor="gray.200"
                >
                  <Box flexShrink={0} color="btn-green-bg">
                    <Icon />
                  </Box>
                  <Text variant="body-md-reg">{label}</Text>
                </Flex>
              ))}
            </Box>

            <Flex
              bg="btn-yellow-bg"
              borderRadius="lg"
              p="space-16"
              gap="space-16"
              align="flex-start"
            >
              <Box color="btn-green-bg">
                <InfoIcon />
              </Box>
              <Text variant="body-sm-reg">
                Une fois confirmée, votre évolution vers Shine Facture est
                définitive. Votre compte sera créé et vos données ci-dessus
                migrées. Vous pourrez récupérer vos documents en format PDF via
                les paramètres de {productName}.
              </Text>
            </Flex>

            <VStack spacing="space-16" pt="space-8">
              <ShiButton
                variant="primary"
                onClick={onConfirm}
                isLoading={isLoading}
                isDisabled={isLoading}
                hasArrow={false}
              >
                Confirmer et migrer
              </ShiButton>
              <ShiButton
                variant="secondary"
                onClick={onClose}
                isDisabled={isLoading}
                hasArrow={false}
              >
                Annuler
              </ShiButton>
            </VStack>
          </VStack>
        )}
      </ModalBody>
    </ModalContent>
  </Modal>
  );
};

export default MigrationModal;
